import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  BookOpen, Plus, Edit2, Trash2, X, Save, AlertCircle, ExternalLink,
  Eye, EyeOff, Image as ImageIcon, Upload, Link2, Loader2,
} from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

const DEFAULT_CATEGORIES = [
  "Practice", "NDIS", "DVA", "DFV", "Athlete Wellbeing", "Community", "Case notes",
];

export default function BlogAdmin() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);        // post being edited (null when hidden, {} for new)
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/posts/admin`);
      setPosts(data || []);
    } catch (e) {
      console.error("Failed to load posts:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently? To hide it from the public site without losing it, edit and uncheck 'Published' instead.")) return;
    try {
      await axios.delete(`${API}/posts/${id}`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't delete post.");
    }
  };

  const togglePublish = async (post) => {
    try {
      await axios.put(`${API}/posts/${post.id}`, { published: !post.published });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't update post.");
    }
  };

  return (
    <div data-testid="blog-admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2 flex items-center gap-2">
            <BookOpen className="w-4 h-4" /> Blog admin
          </div>
          <h1 className="font-display text-4xl text-[#1F2A2E]">Write &amp; edit posts</h1>
          <p className="text-[#4A595E] mt-2">Manage what appears on the public /blog page. Drafts stay hidden until you publish.</p>
        </div>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid="new-post-btn">
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-[#F0E5DE]" data-testid="blog-admin-empty">
          <BookOpen className="w-12 h-12 mx-auto text-[#F4C3CC] mb-4" />
          <h3 className="font-display text-xl text-[#1F2A2E] mb-2">No posts yet</h3>
          <p className="text-[#6B7A80] text-sm mb-6">Write your first essay for the public blog.</p>
          <button onClick={() => setEditing({})} className="btn-primary text-sm">Start writing</button>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-[#F0E5DE] overflow-hidden" data-testid="blog-admin-list">
          <table className="w-full">
            <thead className="bg-[#F1E4D5] text-left">
              <tr>
                <Th>Post</Th>
                <Th>Category</Th>
                <Th>Status</Th>
                <Th>Author</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0E5DE]">
              {posts.map((p) => (
                <tr key={p.id} className="hover:bg-[#F1E4D5]/50 transition align-top" data-testid={`post-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-start gap-3">
                      {p.cover_image ? (
                        <img src={p.cover_image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-[#F1E4D5] flex items-center justify-center shrink-0">
                          <ImageIcon className="w-4 h-4 text-[#C9B4A9]" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-[#1F2A2E] leading-snug">{p.title}</div>
                        <div className="text-xs text-[#6B7A80] mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>/{p.slug}</span>
                          <span>·</span>
                          <span>{p.read_time}</span>
                          {p.published && (
                            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-[#E2718A] hover:underline flex items-center gap-1" data-testid={`view-post-${p.id}`}>
                              <ExternalLink className="w-3 h-3" /> View live
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#284952]">{p.category || "-"}</td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => togglePublish(p)}
                      className={`text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 transition ${
                        p.published ? "bg-[#DCEFE0] text-[#1F6B3A] hover:bg-[#c9e3cf]" : "bg-[#F0E5DE] text-[#6B7A80] hover:bg-[#E5D6CC]"
                      }`}
                      title={p.published ? "Click to unpublish" : "Click to publish"}
                      data-testid={`toggle-publish-${p.id}`}
                    >
                      {p.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {p.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-sm text-[#284952]">{p.author}</td>
                  <td className="px-5 py-4 text-right whitespace-nowrap">
                    <div className="flex justify-end gap-3">
                      <button onClick={() => setEditing(p)} className="text-[#284952] hover:text-[#E2718A]" data-testid={`edit-post-${p.id}`} aria-label="Edit post">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button onClick={() => remove(p.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-post-${p.id}`} aria-label="Delete post">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && (
        <PostEditor
          post={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-5 py-3 text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">{children}</th>;
}

function PostEditor({ post, onClose, onSaved }) {
  const { user } = useAuth();
  const isEdit = !!post?.id;
  const contentRef = useRef(null);
  const [form, setForm] = useState({
    title: post?.title || "",
    slug: post?.slug || "",
    excerpt: post?.excerpt || "",
    content: post?.content || "",
    author: post?.author || user?.name || "Navica Supports",
    category: post?.category || "Practice",
    read_time: post?.read_time || "5 min read",
    cover_image: post?.cover_image || "",
    published: post?.published !== undefined ? post.published : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [imageDialog, setImageDialog] = useState(null);  // null | 'inline' | 'cover'
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverFileRef = useRef(null);

  const wordCount = (form.content || "").trim().split(/\s+/).filter(Boolean).length;

  // Auto-suggest read time from word count when writing
  const estimateReadTime = () => {
    const minutes = Math.max(1, Math.round(wordCount / 220));
    setForm((f) => ({ ...f, read_time: `${minutes} min read` }));
  };

  const insertImageIntoContent = (url, alt = "") => {
    const cleanAlt = (alt || "").trim().replace(/[[\]]/g, "");
    const md = `\n\n![${cleanAlt}](${url})\n\n`;
    const ta = contentRef.current;
    if (ta) {
      const start = ta.selectionStart ?? form.content.length;
      const end = ta.selectionEnd ?? form.content.length;
      const next = form.content.slice(0, start) + md + form.content.slice(end);
      setForm((f) => ({ ...f, content: next }));
      // Restore cursor after the inserted markdown
      requestAnimationFrame(() => {
        const pos = start + md.length;
        ta.focus();
        try { ta.setSelectionRange(pos, pos); } catch { /* ignore */ }
      });
    } else {
      setForm((f) => ({ ...f, content: (f.content || "") + md }));
    }
  };

  const uploadCoverFile = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/blog/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = `${API}${data.url.replace(/^\/api/, "")}`;
      setForm((f) => ({ ...f, cover_image: url }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't upload the cover image.");
    } finally {
      setUploadingCover(false);
      if (coverFileRef.current) coverFileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      // Only send filled fields on edit; on create, send all
      const payload = { ...form };
      if (isEdit) {
        await axios.put(`${API}/posts/${post.id}`, payload);
      } else {
        await axios.post(`${API}/posts`, payload);
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't save the post.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="post-editor-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#F0E5DE] sticky top-0 bg-white z-10 rounded-t-[2rem]">
          <div>
            <h2 className="font-display text-2xl text-[#1F2A2E]">{isEdit ? "Edit post" : "New post"}</h2>
            {isEdit && <div className="text-xs text-[#6B7A80] mt-0.5">/{post.slug}</div>}
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="post-editor-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        <form onSubmit={submit} className="p-7 space-y-5">
          <Field label="Title">
            <input
              className="input-navica"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              maxLength={140}
              required
              placeholder="What's this post called?"
              data-testid="pe-title"
            />
          </Field>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Slug (URL)" hint="Auto-generated from title on save if empty.">
              <div className="flex items-stretch">
                <span className="px-3 flex items-center bg-[#F1E4D5] border border-r-0 border-[#E5D6CC] rounded-l-xl text-xs text-[#6B7A80]">/blog/</span>
                <input
                  className="input-navica !rounded-l-none"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="auto"
                  data-testid="pe-slug"
                />
              </div>
            </Field>

            <Field label="Category">
              <input
                list="blog-categories"
                className="input-navica"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                placeholder="Practice"
                data-testid="pe-category"
              />
              <datalist id="blog-categories">
                {DEFAULT_CATEGORIES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </Field>
          </div>

          <Field label="Excerpt" hint={`Shown on blog index cards. ${form.excerpt.length}/280`}>
            <textarea
              rows={2}
              className="input-navica resize-none"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              maxLength={280}
              required
              placeholder="One or two sentences that hook the reader."
              data-testid="pe-excerpt"
            />
          </Field>

          <Field label="Content" hint={`Write freely. Blank lines create paragraphs on the public page. Add images with the button below. ${wordCount} words · ~${Math.max(1, Math.round(wordCount / 220))} min read.`}>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setImageDialog("inline")}
                className="btn-outline text-xs !py-1.5 !px-3 inline-flex items-center gap-1.5"
                data-testid="insert-image-btn"
              >
                <ImageIcon className="w-3.5 h-3.5" /> Insert image
              </button>
              <span className="text-xs text-[#6B7A80]">Inserts at cursor position. Renders inline on the public page.</span>
            </div>
            <textarea
              ref={contentRef}
              rows={14}
              className="input-navica resize-y font-serif text-base leading-relaxed"
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              required
              placeholder={"Start writing...\n\nSeparate paragraphs with a blank line.\n\n![Optional caption](https://example.com/image.jpg)"}
              data-testid="pe-content"
            />
            <InlineImagePreview content={form.content} />
          </Field>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Cover image" hint="Optional. Upload from your device or paste any public URL.">
              <div className="flex gap-2">
                <input
                  className="input-navica flex-1"
                  value={form.cover_image}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                  placeholder="https://..."
                  data-testid="pe-cover"
                />
                <input
                  ref={coverFileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={(e) => uploadCoverFile(e.target.files?.[0])}
                  className="hidden"
                  data-testid="pe-cover-file"
                />
                <button
                  type="button"
                  onClick={() => coverFileRef.current?.click()}
                  disabled={uploadingCover}
                  className="btn-outline text-xs whitespace-nowrap inline-flex items-center gap-1.5"
                  data-testid="pe-cover-upload-btn"
                >
                  {uploadingCover ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  {uploadingCover ? "Uploading..." : "Upload"}
                </button>
              </div>
            </Field>

            <Field label="Read time" hint="Tap 'Auto' to estimate from word count.">
              <div className="flex gap-2">
                <input
                  className="input-navica flex-1"
                  value={form.read_time}
                  onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                  placeholder="5 min read"
                  data-testid="pe-read-time"
                />
                <button type="button" onClick={estimateReadTime} className="btn-outline text-xs whitespace-nowrap">Auto</button>
              </div>
            </Field>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Author">
              <input
                className="input-navica"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                placeholder="Navica Supports"
                data-testid="pe-author"
              />
            </Field>

            <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl bg-[#F1E4D5] border border-[#F0E5DE] self-end">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                className="navica-check mt-0.5"
                data-testid="pe-published"
              />
              <span className="text-sm text-[#284952]">
                <span className="font-semibold flex items-center gap-1.5">
                  {form.published ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {form.published ? "Publish immediately" : "Save as draft"}
                </span>
                <span className="block text-xs text-[#6B7A80] mt-0.5">
                  {form.published ? "Live on /blog as soon as you save." : "Hidden from the public site. You can publish later."}
                </span>
              </span>
            </label>
          </div>

          {form.cover_image && (
            <div className="rounded-2xl overflow-hidden border border-[#F0E5DE]" data-testid="pe-cover-preview">
              <img src={form.cover_image} alt="Cover preview" className="w-full h-56 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#F0E5DE]">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving || !form.title.trim() || !form.excerpt.trim() || !form.content.trim()} className="btn-primary" data-testid="pe-submit">
              {saving ? "Saving..." : isEdit ? "Save changes" : (form.published ? "Publish" : "Save draft")} <Save className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>

      {imageDialog === "inline" && (
        <ImageInsertDialog
          onClose={() => setImageDialog(null)}
          onInsert={(url, alt) => { insertImageIntoContent(url, alt); setImageDialog(null); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Image insert dialog (upload or paste URL)
// ───────────────────────────────────────────────────────────────

function ImageInsertDialog({ onClose, onInsert }) {
  const [mode, setMode] = useState("upload"); // 'upload' | 'url'
  const [url, setUrl] = useState("");
  const [alt, setAlt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [uploaded, setUploaded] = useState(null); // { url, filename }
  const fileRef = useRef(null);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/blog/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Convert /api/blog/images/{id} to a full URL usable in <img>
      const finalUrl = `${API}${data.url.replace(/^\/api/, "")}`;
      setUploaded({ url: finalUrl, filename: data.filename });
      setUrl(finalUrl);
      // Immediately insert into the post, most users expect "upload = insert"
      onInsert(finalUrl, alt);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't upload the image.");
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    const finalUrl = (url || "").trim();
    if (!finalUrl) {
      setError("Please upload or paste an image URL.");
      return;
    }
    onInsert(finalUrl, alt);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="image-insert-dialog">
      <div className="bg-white rounded-[2rem] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#F0E5DE]">
          <h3 className="font-display text-xl text-[#1F2A2E]">Insert image</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="image-dialog-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex rounded-full bg-[#F1E4D5] p-1 text-sm">
            <button
              type="button"
              onClick={() => setMode("upload")}
              className={`flex-1 py-2 rounded-full font-medium inline-flex items-center justify-center gap-2 transition ${mode === "upload" ? "bg-white text-[#1F2A2E] shadow-sm" : "text-[#6B7A80]"}`}
              data-testid="image-mode-upload"
            >
              <Upload className="w-4 h-4" /> Upload
            </button>
            <button
              type="button"
              onClick={() => setMode("url")}
              className={`flex-1 py-2 rounded-full font-medium inline-flex items-center justify-center gap-2 transition ${mode === "url" ? "bg-white text-[#1F2A2E] shadow-sm" : "text-[#6B7A80]"}`}
              data-testid="image-mode-url"
            >
              <Link2 className="w-4 h-4" /> URL
            </button>
          </div>

          {mode === "upload" ? (
            <div>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => doUpload(e.target.files?.[0])}
                className="hidden"
                data-testid="image-file-input"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="w-full border-2 border-dashed border-[#F0E5DE] hover:border-[#E2718A] hover:bg-[#F1E4D5] transition rounded-2xl py-8 flex flex-col items-center gap-2 text-[#284952]"
                data-testid="image-upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-8 h-8 text-[#E2718A] animate-spin" />
                    <span className="text-sm">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-[#E2718A]" />
                    <span className="text-sm font-medium">Click to choose an image</span>
                    <span className="text-xs text-[#6B7A80]">JPG, PNG, GIF or WEBP · max 10 MB</span>
                  </>
                )}
              </button>
              {uploaded && (
                <div className="mt-3 p-2 bg-[#DCEFE0] border border-[#B9DDBF] rounded-xl text-xs text-[#1F6B3A] flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 shrink-0" /> Uploaded: <span className="truncate">{uploaded.filename}</span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Image URL</label>
              <input
                type="url"
                className="input-navica"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://images.unsplash.com/..."
                data-testid="image-url-input"
              />
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Caption / alt text (optional)</label>
            <input
              className="input-navica"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="A short description of the image"
              data-testid="image-alt-input"
            />
            <p className="text-xs text-[#6B7A80] mt-1.5">Shown to screen readers and as a caption below the image.</p>
          </div>

          {url && (
            <div className="rounded-2xl overflow-hidden border border-[#F0E5DE]">
              <img src={url} alt="Preview" className="w-full h-40 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="button" onClick={submit} disabled={!url || uploading} className="btn-primary" data-testid="image-insert-submit">
              Insert image
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#6B7A80] mt-1.5">{hint}</p>}
    </div>
  );
}

function InlineImagePreview({ content }) {
  // Match markdown image syntax and render thumbnails so the writer can see
  // exactly which images are embedded in the post.
  const matches = Array.from((content || "").matchAll(/!\[([^\]]*)\]\((\S+?)\)/g));
  if (matches.length === 0) return null;
  return (
    <div className="mt-3 p-3 rounded-2xl border border-[#F0E5DE] bg-[#F1E4D5]" data-testid="pe-inline-preview">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-wider font-semibold text-[#6B7A80]">Images in this post</div>
        <div className="text-xs text-[#6B7A80]">{matches.length}</div>
      </div>
      <div className="flex flex-wrap gap-2">
        {matches.map((m, i) => (
          <div key={`${m[2]}-${i}`} className="relative group">
            <img
              src={m[2]}
              alt={m[1] || ""}
              className="w-24 h-24 rounded-xl object-cover border border-[#F0E5DE]"
              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
            />
            <div className="hidden w-24 h-24 rounded-xl border border-dashed border-red-300 bg-red-50 items-center justify-center text-[10px] text-red-500 text-center px-2">
              Won&apos;t load
            </div>
            {m[1] && <div className="text-[10px] text-[#6B7A80] mt-1 max-w-[96px] truncate" title={m[1]}>{m[1]}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
